import { apiRequest } from '../lib/api-client';

/** The leads screen: the figures above the list, and the last thing that happened to each row. */

export const LEAD_FUNNEL = ['NEW', 'CONTACTED', 'QUALIFIED', 'CONVERTED'] as const;

export type LeadStatus = (typeof LEAD_FUNNEL)[number] | 'UNQUALIFIED';

export interface SourcePerformance {
  /** The channel's name, or null when nobody recorded one — named in the browser, not the API. */
  label: string | null;
  total: number;
  converted: number;
  conversionRate: number | null;
  share: number | null;
}

export interface LeadSummary {
  period: { days: number; from: string };
  counts: Partial<Record<LeadStatus, number>>;
  total: number;
  rates: { qualification: number | null; conversion: number | null; close: number | null };
  response: { median: number | null; average: number | null; measured: number };
  untouched: { total: number; stale: number; oldest: string | null };
  averageScore: number | null;
  sources: SourcePerformance[];
  /** Why the disqualified ones ended. A null reason means closed with nothing written down. */
  lossReasons: { reason: string | null; total: number }[];
  /** Medians in days, and how many attempts were raised on people already in the CRM. */
  durations: { toQualify: number | null; toConvert: number | null; fromContacts: number };
}

export function leadSummary(days: number): Promise<LeadSummary> {
  return apiRequest(`/v1/crm/leads/summary?days=${days}`);
}

export interface LeadActivity {
  leadId: string;
  at: string;
  type: string;
  subject: string | null;
}

export function leadActivity(ids: string[]): Promise<{ activity: LeadActivity[] }> {
  if (ids.length === 0) return Promise.resolve({ activity: [] });

  return apiRequest(`/v1/crm/leads/activity?ids=${ids.join(',')}`);
}

/**
 * How far along the funnel a status sits, 0 to 1, and null for a lead that was disqualified.
 *
 * The same rule the server applies, and deliberately duplicated rather than fetched: it is four
 * words of arithmetic, and a round trip to find out how wide to draw a bar would be absurd. The
 * server's copy is the one under test (`lead-metrics.test.ts`); this one is checked against it by
 * `leads-api.test.ts`.
 */
export function funnelProgress(status: string): number | null {
  const index = (LEAD_FUNNEL as readonly string[]).indexOf(status);

  return index < 0 ? null : (index + 1) / LEAD_FUNNEL.length;
}

/** "18 min", "3 h", "2 d" — a response time nobody has to divide in their head. */
export function formatMinutes(minutes: number | null): string {
  if (minutes === null) return '—';
  if (minutes < 60) return `${Math.round(minutes)} min`;
  if (minutes < 60 * 24) return `${(minutes / 60).toFixed(1)} h`;

  return `${(minutes / (60 * 24)).toFixed(1)} d`;
}

/** A rate as a percentage, or a dash when there was nothing to compute it from. */
export function formatRate(value: number | null): string {
  return value === null ? '—' : `${(value * 100).toFixed(1)}%`;
}

export interface NewLead {
  name: string;
  email?: string | null;
  phone?: string | null;
  companyName?: string | null;
  jobTitle?: string | null;
  sourceId?: string | null;
  ownerId?: string | null;
}

export function createLead(input: NewLead): Promise<{ id: string }> {
  return apiRequest('/v1/crm/leads', { method: 'POST', body: input });
}
